// chat/actions.ts — the chat control executor (cloned from Oriora Expense's src/chat/actions.ts,
// re-derived against THIS app's own store: src/store.ts).
//
// The client router (chat/brain.ts) maps ONE user message → ONE ChatAction; this file EXECUTES that
// action against the on-device store. Everything here is LOCAL SQLite over Tauri IPC — no network,
// no billing — with ONE exception: the `summarize` action reuses the app's EXISTING live endpoint
// (summarizeTrends → /api/apps/workout_tracker/summarize_trends). That is the only billed step, and it
// is only reached when the user explicitly asks for a summary, so idle chit-chat never bills.
//
// The action set MIRRORS what the store supports — log a workout, log a set, delete (with confirm),
// filter/search sessions, list PRs, summarize. The store has no update command, so there is
// deliberately NO edit action here (we don't fabricate capabilities the app doesn't have).
//
// HITL discipline (mirrors Expense): the destructive actions (delete_workout / delete_set) are NEVER
// executed from a chat turn on their own — the Chat UI holds them and requires an explicit Confirm tap
// first. This executor just performs the delete once the UI hands it back.

import { store, plateBreakdown, DEFAULT_BAR_KG, DEFAULT_BAR_LB, type Workout, type Routine, type NewRoutine } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "workout-tracker";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { summarizeTrends, BrainError } from "../brain";
import { formatWeight, prLabels } from "../policy";

// A local filter over the workout list (there is no server query endpoint for this app —
// searching is done on-device over listWorkouts()).
export interface WorkoutFilter {
  text?: string;   // matches the workout name (case-insensitive substring)
  since?: string;  // YYYY-MM-DD inclusive
  until?: string;  // YYYY-MM-DD inclusive
  label?: string;
}

export function applyWorkoutFilter(all: Workout[], f: WorkoutFilter): Workout[] {
  const term = f.text?.trim().toLowerCase();
  return all.filter((w) => {
    if (term && !w.name.toLowerCase().includes(term)) return false;
    const d = w.performed_on.slice(0, 10);
    if (f.since && d < f.since) return false;
    if (f.until && d > f.until) return false;
    return true;
  });
}

// A compact row rendered inline in a chat bubble (filter / PR results). Generic title+subtitle so the
// same renderer handles both workouts and PR lines without app-specific coupling.
export interface ResultRow {
  id: string;
  title: string;
  subtitle: string;
  right?: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_workout"; name: string; performed_on: string; notes?: string | null }
  | { kind: "add_set"; workout_id?: string | null; workout_name?: string; performed_on?: string; exercise_slug: string; exercise_name: string; weight: number; reps: number }
  | { kind: "delete_workout"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "delete_set"; id: string; label?: string }      // UI confirms before this reaches executeAction
  | { kind: "filter"; filter: WorkoutFilter; label?: string }
  | { kind: "list_prs" }
  | { kind: "summarize"; days?: number }
  | { kind: "list_routines" }
  | { kind: "add_routine"; input: NewRoutine; note?: string }
  | { kind: "delete_routine"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "start_routine"; id: string; label?: string }
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember"; instruction: string }
  | { kind: "open_program_flow" } // "draft me a program" — opens ProgramSection's draft flow (PROGRAM-DESIGN.md fence changes)
  | { kind: "repeat_workout" } // "repeat my last workout" — recreates the most recent workout, same mechanism as the row's Repeat button
  | { kind: "plate_calc"; target: number; bar?: number | null; unit?: "kg" | "lb" }; // pure on-device plate math (Strong gates this behind Pro)

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // the log changed → the main list should refresh
  rows?: ResultRow[]; // matches to render inline
}

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s: string) =>
  s ? new Date(s.slice(0, 10) + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "";

export async function executeAction(a: ChatAction, ctx?: { transcript?: string; openProgramFlow?: () => void; repeatLastWorkout?: () => Promise<Workout | null>; startRoutine?: (id: string) => Promise<Routine | null> }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_workout": {
        const w = await store.createWorkout({
          name: a.name,
          performed_on: a.performed_on,
          notes: a.notes ?? null,
        });
        return { ok: true, message: `Started "${w.name}" on ${fmtDate(w.performed_on)}. Log sets by typing e.g. "log bench 100 x 5".`, changed: true };
      }

      case "add_set": {
        // Resolve the target workout: use the one the router picked, else create one for the day so a
        // set never has nowhere to live (mirrors tapping "New workout" then "Log a set").
        let wid = a.workout_id ?? null;
        if (!wid) {
          const w = await store.createWorkout({ name: a.workout_name || "Workout", performed_on: a.performed_on || todayISO() });
          wid = w.id;
        }
        const s = await store.addSet({
          workout_id: wid,
          exercise_slug: a.exercise_slug,
          exercise_name: a.exercise_name,
          weight: a.weight,
          reps: a.reps,
        });
        const load = s.weight > 0 ? `${formatWeight(s.weight)} × ${s.reps}` : `${s.reps}`;
        const pr = s.is_pr && s.pr_types.length ? ` — 🏆 new PR (${prLabels(s.pr_types).join(" + ")})!` : "";
        return { ok: true, message: `Logged ${s.exercise_name} ${load}${pr}.`, changed: true };
      }

      case "delete_workout":
        await store.deleteWorkout(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the workout"}.`, changed: true };

      case "delete_set":
        await store.deleteSet(a.id);
        return { ok: true, message: `Deleted the ${a.label ?? "set"}.`, changed: true };

      case "filter": {
        const all = await store.listWorkouts(50000);
        const matches = applyWorkoutFilter(all, a.filter);
        const totalSets = matches.reduce((s, w) => s + w.total_sets, 0);
        const totalVol = matches.reduce((s, w) => s + w.total_volume, 0);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        const rows: ResultRow[] = matches.slice(0, 50).map((w) => ({
          id: w.id,
          title: w.name,
          subtitle: `${fmtDate(w.performed_on)} · ${w.total_sets} set${w.total_sets !== 1 ? "s" : ""}`,
          right: `${formatWeight(w.total_volume)} vol`,
        }));
        const head = `${matches.length} workout${matches.length !== 1 ? "s" : ""}${forLabel} · ${totalSets} set${totalSets !== 1 ? "s" : ""} · ${formatWeight(totalVol)} volume`;
        return { ok: true, message: head + (matches.length > 50 ? " (showing first 50)" : ""), rows };
      }

      case "list_prs": {
        const prs = await store.listPRs();
        if (!prs.length) return { ok: true, message: "No personal records yet — log some sets and they'll show up here." };
        const rows: ResultRow[] = prs.slice(0, 50).map((p) => ({
          id: p.exercise_slug,
          title: p.exercise_name,
          subtitle: `${formatWeight(p.heaviest_weight)} × ${p.heaviest_weight_reps} · 1RM ${formatWeight(p.estimated_one_rm)}`,
          right: `${formatWeight(p.best_session_volume)} vol`,
        }));
        return { ok: true, message: `${prs.length} exercise${prs.length !== 1 ? "s" : ""} with personal records:`, rows };
      }

      case "plate_calc": {
        const unit = a.unit ?? "kg";
        const bar = a.bar != null && a.bar >= 0 ? a.bar : (unit === "kg" ? DEFAULT_BAR_KG : DEFAULT_BAR_LB);
        if (!Number.isFinite(a.target) || a.target <= 0) return { ok: false, message: "Give me a target weight — e.g. \"what plates for 100kg\"." };
        const r = plateBreakdown(a.target, bar, unit);
        if (r.perSide.length === 0) return { ok: true, message: `Just the ${bar}${unit} bar — no plates needed for ${a.target}${unit}.` };
        const perSide = r.perSide.join(` + `);
        const short = r.leftover > 0 ? ` (that's ${r.loadable}${unit} — ${r.leftover}${unit} short of ${a.target}${unit} with standard plates)` : "";
        return { ok: true, message: `For ${a.target}${unit} on a ${bar}${unit} bar, load per side: ${perSide} ${unit}.${short}` };
      }

      case "summarize": {
        const signals = await store.trendSignals(a.days ?? 14);
        if (!signals || signals.workouts <= 0 || signals.total_sets <= 0) {
          return { ok: true, message: "No sets in the last 14 days yet — log a few, then ask me to summarize." };
        }
        try {
          const r = await summarizeTrends(signals);
          const chips: string[] = [];
          if (typeof r.trends.freq_per_week === "number") chips.push(`${r.trends.freq_per_week}×/week`);
          if (r.trends.volume_trend) chips.push(`volume ${r.trends.volume_trend}`);
          if (typeof r.trends.prs_in_window === "number" && r.trends.prs_in_window > 0) chips.push(`${r.trends.prs_in_window} PR${r.trends.prs_in_window !== 1 ? "s" : ""}`);
          const tail = chips.length ? `\n\n${chips.join(" · ")}` : "";
          return { ok: true, message: (r.trends.summary_text || "No summary available.") + tail };
        } catch (e) {
          // BrainError carries user-friendly copy (add funds / sign in / offline) — surface it verbatim.
          if (e instanceof BrainError) return { ok: false, message: e.message };
          throw e;
        }
      }

      case "list_routines": {
        const routines = await store.listRoutines();
        if (!routines.length) return { ok: true, message: 'No routines saved yet — say e.g. "save a push day routine with bench, overhead press and dips".' };
        const rows: ResultRow[] = routines.map((r) => ({
          id: r.id,
          title: r.name,
          subtitle: r.exercises.length ? r.exercises.map((e) => e.name).join(" · ") : "No exercises",
        }));
        return { ok: true, message: `${routines.length} routine${routines.length !== 1 ? "s" : ""}:`, rows };
      }

      case "add_routine": {
        const r = await store.createRoutine(a.input);
        return { ok: true, message: `Saved routine "${r.name}" with ${r.exercises.length} exercise${r.exercises.length !== 1 ? "s" : ""}${a.note ?? ""}. Say "start ${r.name}" to begin a workout from it.`, changed: true };
      }

      case "delete_routine":
        await store.deleteRoutine(a.id);
        return { ok: true, message: `Deleted the "${a.label ?? "routine"}" routine.`, changed: true };

      case "start_routine": {
        const started = await ctx?.startRoutine?.(a.id);
        if (!started) return { ok: false, message: "I couldn't find that routine any more." };
        return { ok: true, message: `Started "${started.name}" today, pre-loaded with its exercises — log your sets below.`, changed: true };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // Photo-heavy backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup is photo-heavy, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path (progress photos add up). One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size, progress photos included.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large photo-heavy backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my workouts" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large (it can be, with progress photos), use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole workout log). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      case "open_program_flow": {
        ctx?.openProgramFlow?.();
        return { ok: true, message: "Opening the program builder — I'll draft a full week from your logged numbers and goals." };
      }

      case "repeat_workout": {
        const repeated = await ctx?.repeatLastWorkout?.();
        if (!repeated) return { ok: false, message: "No workouts logged yet — log one first, then I can repeat it." };
        return { ok: true, message: `Started a new "${repeated.name}" today with the same exercises — log your sets below.`, changed: true };
      }

      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
